import {
  WSActions,
  WSCard,
  WSElement,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSList,
  WSLoader,
  WSText,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { OverlaySpinner } from "../../../components/OverlaySpinner";
import { ErrorAlert } from "@components/ErrorAlert";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useSession } from "../../../v3/hooks/useSession";
import { useDraftPersistence } from "../hooks/useDraftPersistence";
import { useDependentTriggerWatch } from "../hooks/useDependentTriggerWatch";
import { useDynamicYupSchema } from "../hooks/useDynamicYupSchema";
import { useModalStaleRevision } from "../modals/ModalStaleRevision";
import { DynamicFormJurisdictionProvider } from "../context/DynamicFormJurisdictionContext";
import { DynamicFormAttestationProvider } from "../context/DynamicFormAttestationContext";
import { selectorTriggerFields } from "../selectors/selectorTriggerFields";
import { selectorVisibleFields } from "../selectors/selectorVisibleFields";
import { IFormStep, selectorFormSteps } from "../selectors/selectorFormSteps";
import { selectorIdentitySectionSplit } from "../selectors/selectorIdentitySectionSplit";
import {
  IFormWizardPage,
  selectorWizardPages
} from "../selectors/selectorWizardPages";
import {
  DynamicFormDataAdapter,
  IFormFieldValidationError,
  IFormNote,
  IFormSchemaSection,
  IGetFormSchemaResponse,
  ISubmitFormResponse
} from "../types";
import { catalogToInitialValues } from "../utils/catalogToInitialValues";
import {
  extractApiErrorInfo,
  extractServerValidationErrors,
  serverErrorsToFieldErrors
} from "../utils/serverErrorsToFieldErrors";
import { isHiddenUiField } from "../utils/fieldRules";
import {
  isPrefilledReadOnlyField,
  stripDraftExcludedKeys,
  stripSharedPiiKeys
} from "../utils/isSharedPiiField";
import {
  getAttestationText,
  getJurisdictionCountry
} from "../utils/formMetadata";
import {
  DynamicFormEngineConfig,
  DynamicFormEngineConfigProvider,
  resolveDynamicFormEngineConfig
} from "../context/DynamicFormEngineConfigContext";
import { DynamicFormFieldRenderer } from "./DynamicFormFieldRenderer";
import { DynamicFormSection } from "./DynamicFormSection";
import { DynamicFormStepper } from "./DynamicFormStepper";
import { DynamicFormNote } from "./DynamicFormNote";
import { PrefilledDataCard } from "./PrefilledDataCard";

type DynamicFormValues = Record<string, unknown>;
type DynamicFormContext = UseFormMethods<DynamicFormValues>;

type Props = {
  response: IGetFormSchemaResponse;
  /**
   * Draft-scope disambiguator only (never sent to the backend, which
   * resolves everything from the form instance id). Two different pairs can
   * share the same payee + catalog form type, so this must distinguish them
   * — the host should pass the form's own payerAccountId, not an engagement
   * id (drafts are keyed by catalog formId, not instance id, so they survive
   * an instance being reissued; this is what keeps that stable across pairs).
   */
  contextId: string;
  signedByName: string;
  /**
   * Compact form name for the wizard rail (and anywhere space is tight) —
   * e.g. "Federal W-4". The full catalog title stays on the overview card in
   * the content column. Falls back to the response title when omitted.
   */
  shortTitle?: string;
  onSubmitSuccess: (response: ISubmitFormResponse) => void;
  /**
   * The host's data layer: submit, value re-evaluation, and cache refreshes.
   * The engine never talks to a backend directly; see DynamicFormDataAdapter.
   */
  adapter: DynamicFormDataAdapter;
  /**
   * Host-surface configuration (domain validators, currency/country defaults,
   * draft namespace, pre-filled-card action). Defaults keep the engine fully
   * domain-agnostic; see DynamicFormEngineConfig.
   */
  engineConfig?: Partial<DynamicFormEngineConfig>;
};

// Key for the wizard's first page (the merged identity/pre-filled card). It is
// FE-generated, so it's prefixed to never collide with a schema step key.
const INTRO_PAGE_KEY = "__prefilledIntro";

// Stable id so re-opens replace the same toast and settle/unmount can close it.
const REEVALUATION_SNACKBAR_ID = "dynamic-form-reevaluating";

const collectInitialServerErrors = (
  sections: IFormSchemaSection[]
): IFormFieldValidationError[] =>
  sections.flatMap(section =>
    section.fields.flatMap(field => field.validation.errors)
  );

const findSignatureFieldKey = (
  sections: IFormSchemaSection[]
): string | undefined => {
  for (const section of sections) {
    const field = section.fields.find(f => f.type === "SignatureBlock");
    if (field) return field.key;
  }
  return undefined;
};

// A step is shown only when it has at least one visible field or note, so
// steps appear/disappear as the server re-evaluates visibility (e.g. the
// Multiple-jobs step). Hidden-UI fields (worksheet intermediates) render
// nothing, so they must not make an otherwise-empty step appear in the rail;
// a description-only card (e.g. a worksheet intro) counts as content.
const stepHasRenderableContent = (step: IFormStep): boolean =>
  step.sections.some(
    section =>
      selectorVisibleFields(section.fields).filter(
        field => !isHiddenUiField(field)
      ).length > 0 ||
      section.notes.length > 0 ||
      section.warnings.length > 0 ||
      Boolean(section.description)
  );

export const DynamicFormRenderer: React.FC<Props> = ({
  response,
  contextId,
  signedByName,
  shortTitle,
  onSubmitSuccess,
  adapter,
  engineConfig: engineConfigOverrides
}) => {
  // v3: the draft-scope owner is the session's account (the payee account id),
  // read synchronously from the token store — never a blocking v1 session query.
  const { accountId: userId } = useSession();
  const engineConfig = useMemo(
    () => resolveDynamicFormEngineConfig(engineConfigOverrides),
    [engineConfigOverrides]
  );
  const draftScope = useMemo(
    () => ({
      payeeId: userId,
      contextId,
      // Intentionally the CATALOG formId (not the instance id): drafts survive
      // instance re-issuance (e.g. rejection re-collection) for the same form.
      formId: response.formId,
      version: response.version
    }),
    [userId, contextId, response.formId, response.version]
  );

  const draft = useDraftPersistence(draftScope, engineConfig.draftKeyNamespace);
  const modalStaleRevision = useModalStaleRevision();
  const { openSnackbar } = useWSSnackbar();

  const draftSnapshot = useRef(draft.read());
  // Sections are re-evaluated server-side when trigger fields change; hold the
  // latest grouped shape locally so visibility / computed / validation update.
  const [sections, setSections] = useState<IFormSchemaSection[]>(
    response.sections
  );
  const [serverErrors, setServerErrors] = useState<IFormFieldValidationError[]>(
    () => collectInitialServerErrors(response.sections)
  );
  const [submitError, setSubmitError] = useState<WSServiceError | null>(null);

  const initialValues = useMemo(
    () =>
      catalogToInitialValues({
        sections: response.sections,
        draft: draftSnapshot.current,
        currentFiling: response.currentFiling,
        metadata: response.metadata
      }),
    [response.sections, response.currentFiling, response.metadata]
  );

  const validationSchema = useDynamicYupSchema(sections, response.metadata, {
    defaultCountry: engineConfig.defaultCountry,
    maskedIdValidator: engineConfig.maskedIdValidator
  });

  const triggerKeys = useMemo(
    () => selectorTriggerFields(sections),
    [sections]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Submit failure UX. Transport/idempotency/cache keys live in the host's
  // adapter; the engine only interprets the error shape and runs the UI.
  const handleSubmitError = async (err: WSServiceError) => {
    const validationErrors = extractServerValidationErrors(err);
    const { status, message: apiMessage, errorType } = extractApiErrorInfo(err);
    setServerErrors(validationErrors);
    // The engine returns 409 for BOTH StaleVersion and PrerequisiteNotMet;
    // the facade preserves the engine code as `errorType` on the body. An
    // absent errorType (facade versions that predate it) falls back to the
    // stale-revision path; any other 409 code takes the generic error path.
    if (status === 409 && errorType === "PrerequisiteNotMet") {
      setSubmitError(null);
      openSnackbar({
        type: "warning",
        message:
          apiMessage ||
          "A prerequisite form must be completed before this one can be submitted."
      });
      // The host's checklist drives which forms are open — let it re-read so
      // the blocking prerequisite shows up.
      await adapter.onPrerequisiteNotMet?.();
      return;
    }
    if (
      status === 409 &&
      (errorType === "StaleVersion" || errorType === undefined)
    ) {
      setSubmitError(null);
      openSnackbar({
        type: "warning",
        message:
          "This form has been updated since you started. Please reload and try again."
      });
      const reload = await modalStaleRevision.open({});
      if (reload) {
        // The host refreshes the schema and everything derived from it (its
        // checklist / requirement statuses), or stale data lingers after the
        // revision bump.
        await adapter.onStaleRevisionReload?.();
      }
      return;
    }
    if (validationErrors.length > 0) {
      // Field-tagged validation errors are surfaced inline under each field
      // via RHF setError in DynamicFormRendererInner; no snackbar.
      setSubmitError(null);
      return;
    }
    setSubmitError(err);
    openSnackbar({
      type: "warning",
      message:
        apiMessage ||
        "Something went wrong while submitting the form. Please try again or contact support if this continues."
    });
  };

  // The wizard renders a single <form>; only the last page shows the submit
  // button. This guards against an Enter keypress on an earlier page (which
  // would otherwise submit the native form prematurely).
  const isLastStepRef = useRef(true);

  const onSubmit = async (data: DynamicFormValues) => {
    if (!isLastStepRef.current) return;
    setSubmitError(null);
    setServerErrors([]);
    const allFields = sections.flatMap(s => s.fields);
    const cleanedData = stripSharedPiiKeys(data, allFields);

    const signatureFieldKey = findSignatureFieldKey(sections);
    const consentFromField =
      signatureFieldKey !== undefined ? data[signatureFieldKey] : undefined;
    const consentFromBlock = (
      data.signature as { consentAccepted?: boolean } | undefined
    )?.consentAccepted;
    // Keep the SignatureBlock field's value in `data`: the forms engine validates
    // it (Required) server-side. We also mirror the consent into the submit
    // `signature` block below.

    setIsSubmitting(true);
    try {
      const submitResponse = await adapter.submit({
        version: response.version,
        data: cleanedData,
        signature: {
          signedBy: signedByName,
          signedAt: new Date().toISOString(),
          consentAccepted:
            consentFromField === true || consentFromBlock === true
        }
      });
      draft.clear();
      onSubmitSuccess(submitResponse);
    } catch (err) {
      await handleSubmitError(err as WSServiceError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DynamicFormEngineConfigProvider config={engineConfig}>
      <DynamicFormJurisdictionProvider
        country={getJurisdictionCountry(
          response.metadata,
          engineConfig.defaultCountry
        )}
      >
        <DynamicFormAttestationProvider
          attestationText={getAttestationText(response.metadata)}
        >
          <WSForm<DynamicFormValues>
            defaultValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {context => (
              <DynamicFormRendererInner
                context={context}
                response={response}
                shortTitle={shortTitle}
                sections={sections}
                setSections={setSections}
                adapter={adapter}
                triggerKeys={triggerKeys}
                submitError={submitError}
                isSubmitting={isSubmitting}
                submitErrorContextKey={engineConfig.submitErrorContextKey}
                serverErrors={serverErrors}
                onDraftWrite={draft.write}
                isLastStepRef={isLastStepRef}
              />
            )}
          </WSForm>
        </DynamicFormAttestationProvider>
        {isSubmitting && <OverlaySpinner />}
      </DynamicFormJurisdictionProvider>
    </DynamicFormEngineConfigProvider>
  );
};

type InnerProps = {
  context: DynamicFormContext;
  response: IGetFormSchemaResponse;
  shortTitle?: string;
  sections: IFormSchemaSection[];
  setSections: React.Dispatch<React.SetStateAction<IFormSchemaSection[]>>;
  adapter: DynamicFormDataAdapter;
  triggerKeys: string[];
  submitError: WSServiceError | null;
  isSubmitting: boolean;
  submitErrorContextKey: DynamicFormEngineConfig["submitErrorContextKey"];
  serverErrors: IFormFieldValidationError[];
  onDraftWrite: (values: Record<string, unknown>) => void;
  isLastStepRef: React.MutableRefObject<boolean>;
};

const DynamicFormRendererInner: React.FC<InnerProps> = ({
  context,
  response,
  shortTitle,
  sections,
  setSections,
  adapter,
  triggerKeys,
  submitError,
  isSubmitting,
  submitErrorContextKey,
  serverErrors,
  onDraftWrite,
  isLastStepRef
}) => {
  const { debouncedValues } = useDependentTriggerWatch({
    control: context.control,
    triggerFieldKeys: triggerKeys
  });

  const reEvaluation = adapter.useReEvaluation({
    values: debouncedValues,
    // Only re-evaluate once a trigger field actually has a value — avoids a
    // redundant fetch (and "Updating form…" flicker) on first load.
    enabled:
      triggerKeys.length > 0 &&
      Object.values(debouncedValues).some(
        value => value !== undefined && value !== null && value !== ""
      )
  });

  // Apply each server re-evaluation ONCE, guarding on the (react-query-stable)
  // `data` identity so we never setState on every render. We refresh only the
  // schema (visibility / computed / options); server validation errors surface at
  // submit, not on every keystroke (which would also flash errors on first load).
  const lastAppliedReEval = useRef<IGetFormSchemaResponse | undefined>(
    undefined
  );
  // Server-computed values of the latest re-evaluation, applied to RHF only
  // AFTER the section update has rendered: RHF v6's setValue is a no-op for an
  // unregistered field, so a field that became visible in the same response
  // must mount (and register) before its computed value can land.
  const pendingComputedRef = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    const data = reEvaluation.data;
    if (!data || data === lastAppliedReEval.current) {
      return;
    }
    lastAppliedReEval.current = data;
    // Collect server-computed values to push into RHF so auto-calculated
    // fields (e.g. the GA G-4 "Total allowances") populate the input and
    // satisfy required validation — the engine returns them on
    // operations.computed.value, not as user input, so RHF never gets them
    // otherwise.
    const computedValues: Record<string, unknown> = {};
    data.sections.forEach(section =>
      section.fields.forEach(field => {
        const computedValue = field.operations.computed?.value;
        if (computedValue !== undefined && computedValue !== null) {
          computedValues[field.key] = computedValue;
        }
      })
    );
    pendingComputedRef.current = computedValues;
    setSections(data.sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reEvaluation.data]);

  // Runs on the render AFTER setSections — newly visible fields have mounted
  // and registered by now (child Controller effects fire before this one), so
  // every computed value lands instead of being silently dropped.
  useEffect(() => {
    const pending = pendingComputedRef.current;
    if (!pending) return;
    pendingComputedRef.current = null;
    Object.entries(pending).forEach(([key, value]) => {
      context.setValue(key, value, { shouldValidate: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  useEffect(() => {
    if (serverErrors.length === 0) return;
    serverErrorsToFieldErrors(context.setError, serverErrors);
  }, [serverErrors, context.setError]);

  const allValues = context.watch();
  useEffect(() => {
    const allFields = sections.flatMap(s => s.fields);
    // Drafts exclude sensitive user-entered values (SSN/EIN) on top of the
    // server-owned keys — they must never sit in localStorage in plaintext.
    onDraftWrite(stripDraftExcludedKeys(allValues, allFields));
  }, [allValues, onDraftWrite, sections]);

  const isReEvaluating = reEvaluation.isFetching;

  // "Updating form…" now surfaces through the app snackbar (design review):
  // opened with a stable id while a re-evaluation is in flight, closed the
  // moment it settles (and on unmount). The long duration is a safety net —
  // the explicit close is the real lifecycle.
  const { openSnackbar, closeSnackbar } = useWSSnackbar();
  useEffect(() => {
    if (isReEvaluating) {
      openSnackbar({
        id: REEVALUATION_SNACKBAR_ID,
        theme: "dark",
        duration: 60000,
        message: (
          <WSFlexBox.CenterY gap="S" wrap="nowrap">
            <WSLoader.Spinner size="XS" />
            <WSText.ParagraphSm color="white">
              Updating form…
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        )
      });
    } else {
      closeSnackbar(REEVALUATION_SNACKBAR_ID);
    }
    // openSnackbar/closeSnackbar are context methods with stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReEvaluating]);
  useEffect(
    () => () => closeSnackbar(REEVALUATION_SNACKBAR_ID),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Read-only identity sections ("Your information") are split from the
  // editable form sections: identity content (and every other pre-filled
  // read-only field) is merged into ONE card on the wizard's FIRST page, while
  // the editable sections group into the wizard's sub-steps.
  const { identitySections, formSections } = useMemo(
    () => selectorIdentitySectionSplit(sections),
    [sections]
  );

  // All pre-filled (read-only) fields across the whole form — shown together
  // in the merged identity card on page 1 (step mode) or in the "Pre-filled
  // data" card of the single-page layout.
  const prefilledFields = useMemo(
    () =>
      sections
        .flatMap(section => selectorVisibleFields(section.fields))
        .filter(isPrefilledReadOnlyField),
    [sections]
  );

  // Identity-section notes render inside the merged card; identity warnings
  // also mark the first page's rail indicator.
  const identityNotes = useMemo(
    () =>
      identitySections.flatMap(section => [
        ...section.warnings,
        ...section.notes
      ]),
    [identitySections]
  );
  const identityHasWarning = useMemo(
    () =>
      identitySections.some(
        section =>
          section.warnings.length > 0 ||
          section.fields.some(field => field.warnings.length > 0)
      ),
    [identitySections]
  );

  // Wizard steps come from the EDITABLE sections only; the legacy single-page
  // layout keeps rendering every section (identity included) unchanged.
  const wizardSteps = useMemo(
    () => selectorFormSteps(formSections),
    [formSections]
  );
  const visibleWizardSteps = useMemo(
    () => wizardSteps.filter(stepHasRenderableContent),
    [wizardSteps]
  );
  const allSteps = useMemo(() => selectorFormSteps(sections), [sections]);
  const visibleAllSteps = useMemo(
    () => allSteps.filter(stepHasRenderableContent),
    [allSteps]
  );

  // Navigation pages: [merged identity page?, ...section sub-steps, sign page]
  // — the certification step (the last one carrying a SignatureBlock field) is
  // a top-level page; the identity page exists only when there is pre-filled
  // read-only content to verify. With <= 1 page we render the original
  // single-page layout.
  const hasIntroPage = prefilledFields.length > 0;
  const pages = useMemo(
    () =>
      selectorWizardPages({
        steps: visibleWizardSteps,
        intro: hasIntroPage
          ? {
              key: INTRO_PAGE_KEY,
              // The rail shows the compact name; the overview card in the
              // content column carries the full title.
              title: shortTitle ?? response.title,
              hasWarning: identityHasWarning
            }
          : undefined
      }),
    [
      visibleWizardSteps,
      hasIntroPage,
      shortTitle,
      response.title,
      identityHasWarning
    ]
  );
  const isStepMode = pages.length > 1;

  // The form-level warning isn't tied to a section; besides the warning card at
  // the top of the content column it also marks the FIRST page in the nav rail
  // so the amber indicator stays persistent across the wizard.
  const hasFormLevelWarning = response.warnings.length > 0;
  const railPages: IFormWizardPage[] = useMemo(
    () =>
      hasFormLevelWarning && pages.length > 0
        ? pages.map((page, index) =>
            index === 0 ? { ...page, hasWarning: true } : page
          )
        : pages,
    [pages, hasFormLevelWarning]
  );

  // Track the active page by its stable KEY, not a positional index. A server
  // re-evaluation can add/remove pages (changing `pages`), and a stored index
  // would then silently point at a different page — the wizard appeared to
  // jump backward. We derive the index from the key each render; if the active
  // page was removed, we clamp the last known index into range so we stay near
  // it instead of jumping.
  const [activePageKey, setActivePageKey] = useState<string | undefined>(
    undefined
  );
  const lastPageIndexRef = useRef(0);
  const indexByKey = pages.findIndex(page => page.key === activePageKey);
  const activePageIndex =
    indexByKey >= 0
      ? indexByKey
      : Math.min(lastPageIndexRef.current, Math.max(pages.length - 1, 0));
  lastPageIndexRef.current = activePageIndex;

  const isLastPage = activePageIndex >= pages.length - 1;
  isLastStepRef.current = !isStepMode || isLastPage;

  const handleContinue = async () => {
    // The identity page carries no editable fields, so it validates trivially.
    const pageFieldKeys = (pages[activePageIndex]?.step?.sections ?? [])
      .flatMap(section => selectorVisibleFields(section.fields))
      .map(field => field.key);
    const valid =
      pageFieldKeys.length === 0 ? true : await context.trigger(pageFieldKeys);
    if (valid) {
      const nextPage = pages[activePageIndex + 1];
      if (nextPage) setActivePageKey(nextPage.key);
    }
  };

  // Surface a validation error that lives on a hidden page: after a genuine
  // submit attempt (we're on the last page) or a server field-rejection, jump to
  // the first page that owns an errored field so its inline error is visible.
  // Gated on isLastStepRef so an Enter keypress on an earlier page (already
  // blocked from submitting by the same ref in onSubmit) doesn't yank the user
  // forward to a later page's error.
  const submitCount = context.formState.submitCount;
  useEffect(() => {
    if (!isStepMode) return;
    if (!isLastStepRef.current && serverErrors.length === 0) return;
    const errorKeys = [
      ...Object.keys(context.formState.errors ?? {}),
      // Group row errors arrive as `group[i].itemKey` — the page lookup
      // matches on field keys, so reduce them to their root (group) key.
      ...serverErrors.map(
        error => String(error.fieldKey).split(/[.[]/)[0] as string
      )
    ];
    if (errorKeys.length === 0) return;
    const keySet = new Set(errorKeys);
    // The identity page owns the identity sections' (read-only) field keys.
    const index = pages.findIndex(page =>
      (page.step ? page.step.sections : identitySections).some(section =>
        section.fields.some(field => keySet.has(field.key))
      )
    );
    if (index >= 0) setActivePageKey(pages[index].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount, serverErrors]);

  // Identity sections may carry Hidden-UI (worksheet intermediate) fields —
  // they render nothing but must stay mounted so their values remain
  // RHF-registered in the submit payload.
  const identityHiddenElements = identitySections.flatMap(section =>
    selectorVisibleFields(section.fields)
      .filter(isHiddenUiField)
      .map(field => <DynamicFormFieldRenderer key={field.key} field={field} />)
  );

  return (
    // Step-mode canvas: rail + content together are capped at 944px and
    // centered, per the Figma. The hosting Layout passes `wideContent` on the
    // form routes so its content column is actually >= 944px (maxWidth alone
    // can't widen past the host). Single-page forms were designed at the
    // Layout's previous 760px column, so that branch self-caps at 760 to keep
    // its rendered width unchanged inside the now-wider column. The library
    // has no fixed-max-width layout primitive (WSContainer caps at 88rem), so
    // the caps are targeted styles — the codebase's established pattern for
    // one-off width constraints.
    <WSList
      gap="XL"
      style={
        isStepMode
          ? { maxWidth: 944, marginLeft: "auto", marginRight: "auto" }
          : { maxWidth: 760, marginLeft: "auto", marginRight: "auto" }
      }
    >
      <ErrorAlert
        context={submitErrorContextKey}
        error={submitError}
        forceShowRawError
      />
      {/* The content column (the page blocks + actions) is rendered at a STABLE
          tree position with a constant key, and the stepper rail is a keyed
          sibling toggled by isStepMode. This keeps the field subtree mounted
          across an isStepMode flip (pages crossing 1↔2) — otherwise swapping
          element types would remount it and RHF (shouldUnregister:true) would
          drop in-progress values. Inside the 944px canvas the grid's 3/9 split
          puts the rail at ~236px (≈ the Figma's 227px column) with the grid's
          own responsive stacking below the "m" breakpoint. */}
      <WSGrid>
        {isStepMode ? (
          <WSGrid.Item key="rail" span={{ m: "3" }}>
            <DynamicFormStepper
              pages={railPages}
              activePageIndex={activePageIndex}
              onPageClick={index => setActivePageKey(pages[index]?.key)}
            />
          </WSGrid.Item>
        ) : null}
        <WSGrid.Item key="content" span={{ m: isStepMode ? "9" : "12" }}>
          <WSList gap="XL">
            {/* Form-overview card (Info-Architecture Figma 1570-44039): the
                FULL form title heads the card, with the form-level warnings,
                description, and notes INSIDE it. It renders only on the first
                wizard page ("form overview") — once the user is on a section
                page the form-level warning is not repeated (its amber marker
                stays on the rail's first item instead). */}
            {!isStepMode || activePageIndex === 0 ? (
              <WSCard
                header={{ label: { text: response.title } }}
                divider="bottom"
              >
                <WSList gap="M">
                  {response.warnings.map((note, i) => (
                    <DynamicFormNote key={`form-warning-${i}`} note={note} />
                  ))}
                  {response.subtitle ? (
                    <WSText.ParagraphSm color="gray600">
                      {response.subtitle}
                    </WSText.ParagraphSm>
                  ) : null}
                  {response.notes.map((note, i) => (
                    <DynamicFormNote key={`form-note-${i}`} note={note} />
                  ))}
                </WSList>
              </WSCard>
            ) : null}
            {/* Render EVERY page's sections so all fields stay mounted and
                RHF-registered; in step mode only the active page is shown. */}
            {isStepMode
              ? pages.map((page, pageIndex) => (
                  <WSElement
                    key={page.key}
                    hidden={pageIndex !== activePageIndex}
                  >
                    <WSList gap="XL">
                      {page.step ? (
                        page.step.sections.map(section => (
                          <DynamicFormSection
                            key={section.key}
                            section={section}
                          />
                        ))
                      ) : (
                        <>
                          {identityHiddenElements}
                          {/* Always presented as "Pre-filled data" (the values
                              span payer AND payee records, so a "Your
                              information" catalog section title would
                              mislabel it) — the component's defaults carry
                              the Figma title + description. */}
                          <PrefilledDataCard
                            fields={prefilledFields}
                            notes={identityNotes}
                          />
                        </>
                      )}
                    </WSList>
                  </WSElement>
                ))
              : visibleAllSteps.map(step => {
                  // Single-page layout (unchanged): pre-filled (read-only)
                  // fields group into a "Pre-filled data" card below the
                  // editable sections.
                  const stepPrefilledFields = step.sections
                    .flatMap(section => selectorVisibleFields(section.fields))
                    .filter(isPrefilledReadOnlyField);
                  return (
                    <WSElement key={step.key}>
                      <WSList gap="XL">
                        {step.sections.map(section => (
                          <DynamicFormSection
                            key={section.key}
                            section={section}
                          />
                        ))}
                        {stepPrefilledFields.length > 0 ? (
                          <PrefilledDataCard fields={stepPrefilledFields} />
                        ) : null}
                      </WSList>
                    </WSElement>
                  );
                })}
            {/* A single full-width action, matching Figma: Submit on the last
                (or only) page, Continue otherwise. Backward navigation is via
                the stepper rail (completed pages are clickable), so there is no
                separate Back button. */}
            <WSActions
              alignment="fill"
              buttons={[
                isLastPage
                  ? {
                      label: "Submit",
                      type: "submit" as const,
                      loading: isSubmitting,
                      disabled: isSubmitting || isReEvaluating
                    }
                  : {
                      label: "Continue",
                      type: "button" as const,
                      onClick: handleContinue,
                      disabled: isReEvaluating
                    }
              ]}
            />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSList>
  );
};
